import { PlusIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { memo, useContext, useEffect, createContext, useState, useRef } from 'react'
import { SettingsContext } from '@/context/settings-context'
import { cn } from '@/lib/utils'
import { StepStatusIcon, ParentStepStatusIcon } from '@/components/icons/WizardStepIcons'
import { WizardProvider, useWizard } from './WizardContext'
import SubmitButton from '@/components/common/SubmitButton'

// Parent Step Context
const ParentStepContext = createContext(null)

// Wizard Step Component
const WizardStep = ({ name, label, className = '', onClick = () => {}, children }) => {
    const { viewModel, setData, currentStepName } = useWizard()
    const parentName = useContext(ParentStepContext)
    const [isActive, setIsActive] = useState(false)
    const { theme } = useContext(SettingsContext)

    useEffect(() => {
        setIsActive(currentStepName === name)
    }, [currentStepName, name])

    const previousCompleteRef = useRef(null)

    useEffect(() => {
        const childrenMap = viewModel[name]?.children || {}
        const keys = Object.keys(childrenMap)
        const complete = keys.length
            ? keys.reduce((sum, k) => sum + (childrenMap[k].complete || 0), 0) / keys.length
            : viewModel[name]?.complete || 0

        // Only update if the complete value actually changed and it's different from what we stored
        if (previousCompleteRef.current !== complete && viewModel[name]?.complete !== complete) {
            previousCompleteRef.current = complete
            setData((prev) => ({
                ...prev,
                [name]: { ...prev[name], complete },
            }))
        }
    }, [viewModel[name]?.children, name, setData, viewModel[name]?.complete])

    const childrenMap = viewModel[name]?.children || {}
    const childNames = Object.keys(childrenMap)
    const childList = Object.values(childrenMap)
    const visibleChildren = childNames.filter((childName) => childrenMap[childName].visible !== false)
    const allVisible = childNames.length > 0 && visibleChildren.length === childNames.length
    const allChildrenHidden = childNames.length > 0 && visibleChildren.length === 0
    const noneCompleted = visibleChildren.every((childName) => (childrenMap[childName].complete || 0) === 0)
    const showGreenCircle = allVisible && noneCompleted

    const progress = parentName ? viewModel[parentName]?.children[name]?.complete || 0 : viewModel[name]?.complete || 0
    const progressOverride = allChildrenHidden ? viewModel[name]?.complete : progress

    return (
        <ParentStepContext.Provider value={name}>
            <div
                className={cn('tw:!bg-transparent tw:text-[13px] tw:gap-3', 'tw:py-1.5 tw:my-1', theme, {
                    'tw:outline-[2px] tw:rounded-lg tw:outline-blue-500 tw:!bg-[#6980AA29]': isActive,
                    'd-none':
                        viewModel[name]?.visible === false || viewModel[parentName]?.children[name]?.visible === false,
                })}
                onClick={onClick}
            >
                <div className={cn('tw:flex tw:items-center tw:gap-3 tw:!pt-0 tw:relative', className)}>
                    {childList.length > 0 && (
                        <ParentStepStatusIcon
                            children={childList}
                            currentStepName={currentStepName}
                            progress={progressOverride}
                        />
                    )}

                    {childList.length === 0 && (
                        <StepStatusIcon showGreenCircle={showGreenCircle} progress={progressOverride} />
                    )}
                    <span>{label}</span>
                </div>
            </div>
            <div>{children}</div>
        </ParentStepContext.Provider>
    )
}

// Content Panel Component
const ContentPanel = ({ screensMap }) => {
    const { currentStepName } = useWizard()

    const Component = screensMap[currentStepName]

    if (!Component)
        return (
            <div className="flex-grow-1 tw:p-6">
                <div className="tw:text-center tw:text-gray-500">No component found for step: {currentStepName}</div>
            </div>
        )

    return (
        <div className="flex-grow-1 overflow-auto">
            <Component />
        </div>
    )
}

// Navigation Component
const Navigation = ({ submitButtonProps = {} }) => {
    const { goBack, goNext, currentStep, stepOrder, customNavigation } = useWizard()

    // If a custom navigation is registered, use it instead
    if (customNavigation) {
        return typeof customNavigation === 'function' ? customNavigation() : customNavigation
    }

    // Default navigation
    const isFirst = currentStep === 0
    const isLast = currentStep === stepOrder.length - 1

    const {
        onClick: submitOnClick,
        buttonText = 'Submit',
        showSpinner = false,
        spinner = 'right',
        disabled = false,
        ...restSubmitButtonProps
    } = submitButtonProps

    return (
        <div className="d-flex gap-2">
            {!isFirst && (
                <Button variant="secondary" onClick={goBack} disabled={isFirst} className="tw:w-[156px] tw:!text-sm">
                    Back
                </Button>
            )}
            {isLast ? (
                <SubmitButton
                    buttonText={buttonText}
                    onClick={async () => {
                        await goNext()
                        if (submitOnClick) {
                            await Promise.resolve(submitOnClick())
                        }
                    }}
                    showSpinner={showSpinner}
                    spinner={spinner}
                    disabled={disabled}
                    {...restSubmitButtonProps}
                />
            ) : (
                <Button onClick={goNext} className="tw:w-[156px] tw:!text-sm">
                    Next
                </Button>
            )}
        </div>
    )
}

// Main Generic Wizard Component
export const GenericWizard = memo(
    ({
        title,
        icon: Icon = PlusIcon,
        storageKey,
        initialState = {},
        idExtractor,
        onStateChange,
        screensMap,
        submitButtonProps = {},
        children,
        showStepper = true,
        subHeader,
        existingId,
        initialContentCache,
        editCreativeId,
        startAtReviewSave,
        startAtStep,
        cloneCreativeId,
        campaignId,
    }) => {
        const { theme } = useContext(SettingsContext)

        useEffect(() => {
            document.body.classList.remove('light', 'dark')
            document.body.classList.add(theme)
        }, [theme])

        return (
            <div
                className={`campaigns-container tw:rounded-sm d-flex flex-column ${theme}`}
                style={{ minHeight: 'calc(100vh - 50px)', overflow: 'hidden' }}
            >
                <WizardProvider
                    storageKey={storageKey}
                    initialState={initialState}
                    idExtractor={idExtractor}
                    onStateChange={onStateChange}
                    existingId={existingId}
                    initialContentCache={initialContentCache}
                    editCreativeId={editCreativeId}
                    startAtReviewSave={startAtReviewSave}
                    startAtStep={startAtStep}
                    cloneCreativeId={cloneCreativeId}
                    campaignId={campaignId}
                >
                    <div className="tw:border-b tw:border-border tw:flex tw:justify-between tw:px-6 tw:py-4 tw:items-center">
                        <div className="tw:flex tw:!items-center tw:gap-2">
                            <Icon size={22} className="tw:text-gray-200" />
                            <h2 className="tw:dark:text-white tw:text-black tw:!text-xl tw:!m-0 tw:!mb-0">{title}</h2>
                        </div>
                        {subHeader && <div>{subHeader}</div>}
                    </div>
                    <div className="d-flex flex-grow-1 overflow-hidden">
                        <nav
                            className={cn(
                                'tw:min-w-[240px] tw:max-w-[240px] tw:border-r tw:border-border tw:px-3 tw:py-4',
                                { 'tw:hidden': !showStepper },
                            )}
                        >
                            {children}
                        </nav>
                        <ContentPanel screensMap={screensMap} />
                    </div>
                    <div className="tw:px-6 tw:py-5 tw:border-t tw:border-border tw:flex tw:!items-center tw:justify-end">
                        <Navigation submitButtonProps={submitButtonProps} />
                    </div>
                </WizardProvider>
            </div>
        )
    },
)

// Export hook for external use
export { useWizard, WizardStep }
